// src/components/ToastStyles.js
import React from 'react';
import { GlobalStyles } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * react-toastify ships a generic panel — a blue info, a neon green success and
 * a 6px radius that belong to no particular site. It is built on CSS variables
 * though, so rather than fight its stylesheet we hand it ours and let it paint
 * itself in the site's own palette.
 *
 * Driving it from the MUI theme means the toasts follow the colour mode for
 * free, and there is still only one place where olive is defined.
 *
 * Success is deliberately the brand olive rather than a signal green: on this
 * site "added to your cart" is a farm-shop message, not a system alert.
 */
const ToastStyles = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  const surface = theme.palette.background.paper;
  const ink = theme.palette.text.primary;
  const muted = theme.palette.text.secondary;

  const TYPES = {
    success: theme.palette.primary.main,
    info: theme.palette.info.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
  };

  // A stripe down the leading edge, so the kind of message is legible before a
  // word of it is read.
  const stripes = Object.entries(TYPES).reduce(
    (rules, [type, color]) => ({
      ...rules,
      [`.Toastify__toast--${type}`]: { borderLeft: `3px solid ${color}` },
    }),
    {},
  );

  return (
    <GlobalStyles
      styles={{
        ':root': {
          // Surface and type, for whichever of the two themes is mounted.
          '--toastify-color-light': surface,
          '--toastify-color-dark': surface,
          '--toastify-text-color-light': ink,
          '--toastify-text-color-dark': ink,

          '--toastify-color-success': TYPES.success,
          '--toastify-color-info': TYPES.info,
          '--toastify-color-warning': TYPES.warning,
          '--toastify-color-error': TYPES.error,

          '--toastify-icon-color-success': TYPES.success,
          '--toastify-icon-color-info': TYPES.info,
          '--toastify-icon-color-warning': TYPES.warning,
          '--toastify-icon-color-error': TYPES.error,

          '--toastify-color-progress-success': TYPES.success,
          '--toastify-color-progress-info': TYPES.info,
          '--toastify-color-progress-warning': TYPES.warning,
          '--toastify-color-progress-error': TYPES.error,
          '--toastify-color-progress-light': TYPES.success,
          '--toastify-color-progress-dark': TYPES.success,

          '--toastify-spinner-color': TYPES.success,
          '--toastify-spinner-color-empty-area': theme.palette.divider,

          '--toastify-font-family': theme.typography.fontFamily,
          '--toastify-toast-width': '360px',
          '--toastify-toast-min-height': '58px',
          '--toastify-toast-padding': '14px',
          '--toastify-toast-bd-radius': '14px',
          '--toastify-toast-offset': '20px',
          '--toastify-toast-shadow': dark
            ? '0 12px 34px rgba(0, 0, 0, 0.55)'
            : '0 12px 34px rgba(60, 54, 40, 0.16)',
        },

        '.Toastify__toast': {
          border: `1px solid ${theme.palette.divider}`,
          // The frosted surface the header and the cart dropdown already use.
          backdropFilter: 'blur(10px) saturate(140%)',
          WebkitBackdropFilter: 'blur(10px) saturate(140%)',
          alignItems: 'center',
          gap: 4,
          paddingRight: 10,
        },

        ...stripes,

        '.Toastify__toast-body': {
          fontSize: '0.9rem',
          fontWeight: 500,
          lineHeight: 1.5,
          color: ink,
          padding: '4px 6px',
        },

        '.Toastify__toast-icon': {
          width: 20,
          marginInlineEnd: 12,
        },

        '.Toastify__close-button': {
          alignSelf: 'center',
          color: muted,
          opacity: 0.7,
          padding: 4,
          borderRadius: 8,
          transition: 'opacity .2s ease, background-color .2s ease',
          '&:hover, &:focus-visible': {
            opacity: 1,
            backgroundColor: theme.palette.action.hover,
          },
        },

        // The default bar is a thick block pinned to the very bottom edge; this
        // is a hairline that reads as a timer rather than a second border.
        '.Toastify__progress-bar': {
          height: 3,
          opacity: 1,
        },
        '.Toastify__progress-bar--wrp': {
          height: 3,
          borderBottomLeftRadius: 14,
          borderBottomRightRadius: 14,
        },
        '.Toastify__progress-bar--bg': {
          opacity: dark ? 0.16 : 0.12,
        },
      }}
    />
  );
};

export default ToastStyles;
